import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardShell from "../components/DashboardShell";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { AlertTriangle, LogOut as LogOutIcon, ShieldCheck, ShieldOff } from "lucide-react";

const TABS = ["Profile", "Security", "Notifications", "Privacy", "Account"];

export default function SettingsPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Profile");
  const [cur, setCur] = useState(""); const [neu, setNeu] = useState("");
  const [vis, setVis] = useState(user?.profile_visibility || "public");
  const [msgFrom, setMsgFrom] = useState(user?.messages_from || "everyone");
  const [delConfirm, setDelConfirm] = useState("");
  const [showHardDelete, setShowHardDelete] = useState(false);
  const [totp, setTotp] = useState({ enabled: false, setup: null });
  const [totpCode, setTotpCode] = useState("");
  const [totpPw, setTotpPw] = useState("");

  useEffect(() => {
    api.get(`/auth/me`).then((r) => setTotp((t) => ({ ...t, enabled: !!r.data?.totp_enabled }))).catch(() => {});
  }, []);

  const startTotp = async () => {
    try { const { data } = await api.post(`/settings/2fa/setup`); setTotp({ enabled: false, setup: data }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmTotp = async () => {
    try { await api.post(`/settings/2fa/confirm`, { code: totpCode }); toast.success("2FA enabled. Future logins will require your authenticator code."); setTotp({ enabled: true, setup: null }); setTotpCode(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const disableTotp = async () => {
    try { await api.post(`/settings/2fa/disable`, { password: totpPw, code: totpCode }); toast.success("2FA disabled."); setTotp({ enabled: false, setup: null }); setTotpCode(""); setTotpPw(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const change = async () => {
    try { await api.post(`/settings/change-password`, { current_password: cur, new_password: neu }); toast.success("Password updated. Other sessions revoked."); setCur(""); setNeu(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const revoke = async () => {
    if (!window.confirm("Log out from all other devices?")) return;
    try { await api.post(`/settings/revoke-sessions`); toast.success("All other sessions revoked"); await signOut(); navigate("/login"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveP = async () => {
    try { await api.patch(`/settings/privacy`, { profile_visibility: vis, messages_from: msgFrom }); toast.success("Privacy saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const deact = async () => {
    if (!window.confirm("Deactivate your account?\n\nYour profile and posts will be hidden. An admin can restore your account within 30 days. You'll be signed out.")) return;
    try { await api.post(`/settings/deactivate`); toast.success("Account deactivated"); await signOut(); navigate("/"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const hardDelete = async () => {
    if (delConfirm !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
    try { await api.post(`/settings/delete-account`, { confirm: "DELETE" }); toast.success("Account permanently deleted"); await signOut(); navigate("/"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell>
      <div className="max-w-3xl mx-auto space-y-5" data-testid="settings-page">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Settings</div>
          <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Account Settings</h1>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-[rgba(0,170,255,0.18)] pb-1">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-xs font-mono-tech uppercase tracking-wider border-b-2 ${tab===t?"border-[#00aaff] text-[#00aaff]":"border-transparent text-[#94a3b8] hover:text-white"}`} data-testid={`settings-tab-${t.toLowerCase()}`}>{t}</button>
          ))}
        </div>

        {tab === "Profile" && (
          <div className="space-y-4" data-testid="profile-tab">
            <IdentitySection />
            <div className="csc-card p-6">
              <p className="text-sm text-[#94a3b8]">Profile editing (bio, avatar, banner, social links) lives in the dedicated profile editor.</p>
              <Link to="/profile/edit" className="csc-btn-primary inline-flex mt-3" data-testid="goto-profile-edit">Edit Profile →</Link>
            </div>
          </div>
        )}

        {tab === "Security" && (
          <div className="space-y-4" data-testid="security-tab">
            <div className="csc-card p-6 space-y-3">
              <h3 className="font-semibold">Change Password</h3>
              <input type="password" value={cur} onChange={(e)=>setCur(e.target.value)} placeholder="Current password" className="csc-input" data-testid="cur-pw" />
              <input type="password" value={neu} onChange={(e)=>setNeu(e.target.value)} placeholder="New password (12+ chars, upper, number, special)" className="csc-input" data-testid="new-pw" />
              <button onClick={change} className="csc-btn-primary" data-testid="change-pw-btn">Update password</button>
            </div>
            <div className="csc-card p-6 space-y-3" data-testid="totp-card">
              <h3 className="font-semibold flex items-center gap-2">
                {totp.enabled
                  ? <><ShieldCheck size={14} className="text-[#39ff14]" /> Two-Factor Authentication · Enabled</>
                  : <><ShieldOff size={14} className="text-[#94a3b8]" /> Two-Factor Authentication</>}
              </h3>
              {!totp.enabled && !totp.setup && (
                <>
                  <p className="text-xs text-[#94a3b8]">Add an extra layer of security with Google Authenticator, Authy, 1Password, or any TOTP app. Strongly recommended for admin accounts.</p>
                  <button onClick={startTotp} className="csc-btn-primary" data-testid="totp-start">Enable 2FA</button>
                </>
              )}
              {!totp.enabled && totp.setup && (
                <>
                  <p className="text-xs text-[#94a3b8]">1. Scan this QR code with your authenticator app (or paste the secret manually). 2. Enter the 6-digit code to confirm.</p>
                  <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <img src={totp.setup.qr} alt="TOTP QR" className="w-44 h-44 rounded-lg bg-white p-2" data-testid="totp-qr" />
                    <div className="flex-1 space-y-2">
                      <div>
                        <div className="csc-label">Manual entry secret</div>
                        <code className="font-mono-tech text-[11px] text-[#00aaff] break-all" data-testid="totp-secret">{totp.setup.secret}</code>
                      </div>
                      <input value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className="csc-input text-center text-xl tracking-[0.4em] font-mono-tech" maxLength={6} data-testid="totp-confirm-code" />
                      <button onClick={confirmTotp} disabled={totpCode.length !== 6} className="csc-btn-primary w-full" data-testid="totp-confirm">Confirm & enable</button>
                    </div>
                  </div>
                </>
              )}
              {totp.enabled && (
                <>
                  <p className="text-xs text-[#94a3b8]">To disable, enter your password and a current 2FA code.</p>
                  <input type="password" value={totpPw} onChange={(e) => setTotpPw(e.target.value)} placeholder="Password" className="csc-input" data-testid="totp-disable-pw" />
                  <input value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className="csc-input text-center" maxLength={6} data-testid="totp-disable-code" />
                  <button onClick={disableTotp} className="csc-btn-ghost hover:!border-red-400 hover:!text-red-400" data-testid="totp-disable">Disable 2FA</button>
                </>
              )}
            </div>

            <div className="csc-card p-6 space-y-3">
              <h3 className="font-semibold flex items-center gap-2"><LogOutIcon size={14} className="text-[#00aaff]" /> Active sessions</h3>
              <p className="text-xs text-[#94a3b8]">You are signed in on this device. To sign out everywhere else (and invalidate any other active tokens), click below.</p>
              <button onClick={revoke} className="csc-btn-ghost" data-testid="revoke-sessions-btn">Log out all other devices</button>
            </div>
          </div>
        )}

        {tab === "Notifications" && (
          <div className="csc-card p-6">
            <p className="text-sm text-[#94a3b8]">Per-category notification toggles live on the dedicated page.</p>
            <Link to="/settings/notifications" className="csc-btn-primary inline-flex mt-3" data-testid="goto-notif-settings">Open notification settings →</Link>
          </div>
        )}

        {tab === "Privacy" && (
          <div className="csc-card p-6 space-y-3" data-testid="privacy-tab">
            <div>
              <label className="csc-label">Profile visibility</label>
              <select value={vis} onChange={(e)=>setVis(e.target.value)} className="csc-input" data-testid="vis-select">
                <option value="public">Public</option><option value="private">Private</option>
              </select>
            </div>
            <div>
              <label className="csc-label">Who can message me</label>
              <select value={msgFrom} onChange={(e)=>setMsgFrom(e.target.value)} className="csc-input" data-testid="msg-from-select">
                <option value="everyone">Everyone</option><option value="following">People I follow</option>
              </select>
            </div>
            <button onClick={saveP} className="csc-btn-primary" data-testid="save-privacy">Save</button>
          </div>
        )}

        {tab === "Account" && (
          <div className="space-y-4" data-testid="account-tab">
            <div className="csc-card p-6 space-y-3">
              <h3 className="font-semibold">Deactivate Account</h3>
              <p className="text-sm text-[#94a3b8]">Hide your profile and posts. Reversible — an admin can restore your account within 30 days. You'll be signed out.</p>
              <button onClick={deact} className="csc-btn-ghost" data-testid="deactivate-btn">Deactivate Account</button>
            </div>

            <div className="csc-card p-6 space-y-3 border-red-500/30" data-testid="hard-delete-card">
              <h3 className="font-semibold flex items-center gap-2 text-red-300"><AlertTriangle size={14} /> Permanently Delete Account</h3>
              <p className="text-sm text-[#94a3b8]">
                <strong>Irreversible.</strong> Your personal data will be removed, and all your posts and comments will be anonymized to "Deleted User" (GDPR / PIPEDA compliant).
                Direct messages will be deleted, follows removed, and votes/reactions erased.
              </p>
              {!showHardDelete ? (
                <button onClick={() => setShowHardDelete(true)} className="csc-btn-ghost hover:!border-red-400 hover:!text-red-400" data-testid="delete-account-open">I want to delete my account…</button>
              ) : (
                <div className="space-y-3 pt-1">
                  <label className="csc-label text-red-300">Type <span className="font-mono-tech">DELETE</span> to confirm</label>
                  <input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} className="csc-input" placeholder="DELETE" data-testid="delete-confirm-input" />
                  <div className="flex gap-2">
                    <button onClick={() => { setShowHardDelete(false); setDelConfirm(""); }} className="csc-btn-ghost" data-testid="delete-cancel">Cancel</button>
                    <button onClick={hardDelete} disabled={delConfirm !== "DELETE"} className="csc-btn-ghost hover:!border-red-400 hover:!text-red-400 disabled:opacity-40" data-testid="delete-account-confirm">Permanently delete</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function IdentitySection() {
  const { user } = useAuth();
  const [allowance, setAllowance] = useState(null);
  const [form, setForm] = useState({ handle: "", first_name: "", last_name: "" });
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/profile/me/handle-allowance`).then((r) => setAllowance(r.data)).catch(() => {});
    setForm({ handle: user?.handle || "", first_name: user?.first_name || "", last_name: user?.last_name || "" });
  }, [user?.handle, user?.first_name, user?.last_name]);

  const save = async () => {
    setErr("");
    try {
      await api.patch(`/profile/me/identity`, form);
      toast.success("Identity updated. Refresh to see changes everywhere.");
      const r = await api.get(`/profile/me/handle-allowance`);
      setAllowance(r.data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="csc-card p-6 space-y-3" data-testid="identity-card">
      <h3 className="font-semibold">Name &amp; Handle</h3>
      {allowance && (
        <div className="font-mono-tech text-[11px] text-[#94a3b8]" data-testid="handle-allowance">
          You have <span className="text-[#39ff14] font-bold">{allowance.remaining}</span> of {allowance.limit} handle changes remaining this year.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="csc-label">First Name</label>
          <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="csc-input" data-testid="identity-first" />
        </div>
        <div>
          <label className="csc-label">Last Name</label>
          <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="csc-input" data-testid="identity-last" />
        </div>
      </div>
      <div>
        <label className="csc-label">Handle (@…)</label>
        <input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value.replace(/[^A-Za-z0-9_]/g, "") })} className="csc-input" data-testid="identity-handle" />
      </div>
      {err && <div className="text-xs text-red-400" data-testid="identity-error">{err}</div>}
      <button onClick={save} className="csc-btn-primary" data-testid="identity-save">Save</button>
    </div>
  );
}
